import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const carouselImages = [
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc5b2697-1768303066231.png",
    alt: "Professional business consultation meeting with modern digital workspace and analytics dashboards"
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_11475bd3b-1767754766246.png",
    alt: "Corporate training session with business professionals using advanced digital tools and AI technology"
  },
  {
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_18901194d-1766749336030.png",
    alt: "Strategic business transformation meeting with international team collaboration and innovation"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages?.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [carouselImages?.length]);

  const handleNavigateToHR = () => {
    navigate('/human-resources-management-services');
  };

  const handleExploreHistory = () => {
    // Scroll to the foundation story section or navigate as needed
    const element = document.getElementById('foundation-story');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative overflow-hidden min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Carousel Background */}
      <div className="absolute inset-0 opacity-20">
        {carouselImages?.map((image, index) =>
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
          index === currentSlide ? 'opacity-100' : 'opacity-0'}`
          }>

            <img
            src={image?.url}
            alt={image?.alt}
            className="w-full h-full object-cover" />

          </div>
        )}
      </div>
      
      {/* Dark overlays with brand colors */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-black/95"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#96121D]/10 via-transparent to-[#9B8E57]/10"></div>
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-[#9B8E57]/15 backdrop-blur-sm rounded-full px-6 py-3 border border-[#9B8E57]/30">
                  <Icon name="History" size={24} className="text-[#9B8E57]" />
                  <span className="text-lg font-medium text-white">18 Ans d'Excellence</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight text-white">
                  À Propos de
                  <span className="block text-[#9B8E57] drop-shadow-lg">CRF Perfection</span>
                </h1>
                
                <p className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-2xl">
                  Une histoire de transformation et d'innovation qui s'étend sur près de deux décennies. 
                  Découvrez notre parcours exceptionnel, de Sinus International à notre renaissance moderne.
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={handleExploreHistory}
                  style={{ backgroundColor: '#96121D', borderRadius: '12px' }}
                  className="hover:opacity-90 text-white font-semibold px-8 py-4 shadow-xl transition-all duration-300 hover:scale-105 border-0 cursor-pointer">

                  <span className="flex items-center justify-center">
                    <span>Explorer Notre Histoire</span>
                    <Icon name="ArrowRight" size={20} className="ml-2" />
                  </span>
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleNavigateToHR}
                  style={{ borderColor: '#9B8E57', color: '#9B8E57', borderRadius: '12px' }}
                  className="border-2 hover:bg-gray-800 font-semibold px-8 py-4 backdrop-blur-sm bg-gray-900/50 transition-all duration-300 cursor-pointer">

                  <span className="flex items-center justify-center">
                    <Icon name="Download" size={20} className="mr-2" />
                    <span>Catalogue des Formations</span>
                  </span>
                </Button>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-8 pt-8">
                <div className="flex items-center space-x-3">
                  <div className="bg-[#9B8E57] rounded-full p-2">
                    <Icon name="Calendar" size={16} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-300 font-medium">Fondé en 2007</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="bg-[#96121D] rounded-full p-2">
                    <Icon name="Globe" size={16} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-300 font-medium">Présence Internationale</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-700 rounded-full p-2">
                    <Icon name="Zap" size={16} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-300 font-medium">Innovation IA</span>
                </div>
              </div>
            </div>
            
            {/* Hero Visual - Simplified Year Display */}
            <div className="relative lg:ml-8">
              <div className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-lg rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
                {/* Year Display */}
                <div className="space-y-8">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-white">Notre Parcours</h3>
                    <Icon name="BookOpen" size={28} className="text-[#9B8E57]" />
                  </div>
                  
                  {/* Years Display - 2007 to 2025 */}
                  <div className="flex items-center justify-center space-x-6 py-12">
                    {/* 2007 - Start Year */}
                    <div className="flex flex-col items-center space-y-4">
                      <div className="bg-gradient-to-br from-[#9B8E57] to-[#9B8E57]/80 rounded-2xl p-6 shadow-xl">
                        <p className="text-5xl font-bold text-white">2007</p>
                      </div>
                      <p className="text-gray-300 text-sm font-medium">Fondation</p>
                    </div>
                    
                    {/* Connecting Line */}
                    <div className="flex-1 flex items-center justify-center">
                      <div className="h-1 w-full bg-gradient-to-r from-[#9B8E57] via-[#96121D] to-[#9B8E57] rounded-full"></div>
                    </div>
                    
                    {/* 2025 - Current Year */}
                    <div className="flex flex-col items-center space-y-4">
                      <div className="bg-gradient-to-br from-[#96121D] to-[#96121D]/80 rounded-2xl p-6 shadow-xl">
                        <p className="text-5xl font-bold text-white">2025</p>
                      </div>
                      <p className="text-gray-300 text-sm font-medium">Aujourd'hui</p>
                    </div>
                  </div>
                  
                  {/* Experience Indicator */}
                  <div className="bg-gradient-to-r from-[#96121D] to-[#9B8E57] rounded-xl p-6 text-center shadow-lg">
                    <p className="text-white text-4xl font-bold">18+</p>
                    <p className="text-white/90 text-base font-medium">Années d'Excellence</p>
                  </div>
                  
                  {/* Additional Info */}
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="bg-gray-800/50 rounded-xl p-4 text-center border border-gray-700/50">
                      <Icon name="Users" size={24} className="text-[#9B8E57] mx-auto mb-2" />
                      <p className="text-white text-sm font-semibold">1000+</p>
                      <p className="text-gray-400 text-xs">Clients Formés</p>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 text-center border border-gray-700/50">
                      <Icon name="Award" size={24} className="text-[#96121D] mx-auto mb-2" />
                      <p className="text-white text-sm font-semibold">50+</p>
                      <p className="text-gray-400 text-xs">Certifications</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-r from-[#9B8E57] to-[#9B8E57]/80 rounded-2xl p-4 shadow-xl">
                <Icon name="Award" size={32} className="text-white" />
              </div>
              
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-gray-800 to-gray-700 rounded-2xl p-4 shadow-xl border border-gray-600">
                <div className="flex items-center space-x-3">
                  <Icon name="TrendingUp" size={24} className="text-[#96121D]" />
                  <div className="text-sm">
                    <p className="font-bold text-white">Croissance</p>
                    <p className="text-gray-300">Continue</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Carousel Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
        {carouselImages?.map((_, index) =>
        <button
          key={index}
          onClick={() => setCurrentSlide(index)}
          style={
          index === currentSlide ?
          { backgroundColor: '#96121D', width: '2rem' } :
          {}
          }
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
          index === currentSlide ?
          '' : 'bg-gray-600 hover:bg-gray-500'}`
          } />

        )}
      </div>
      
      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-16 fill-gray-900">
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
          <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
        </svg>
      </div>
    </section>);

};

export default HeroSection;